import React, { useEffect, useReducer } from 'react'
import { KaEmployeeContext, KaEmployeeState, KaEmployeeStatus } from './KaEmployeeContext';
import { MsalConfiguration, MsalUser, useMsalAuth } from '../../common/lib/hooks/msal/useMsalAuth';
import { clearTracking, trackEmployeeSignIn } from '../tracking';

type EmployeeProfile = {
  canEditSites: boolean;
  canViewOrders: boolean;
}

enum Step {
  NotAuthenticated,
  GettingUserProfile,
  UserDone,
}

enum ActionType {
  GetUserProfile,
  UserProfileAcquired,
}

type State =
  | { step: Step.NotAuthenticated }
  | { step: Step.GettingUserProfile, msalUser: MsalUser }
  | { step: Step.UserDone, msalUser: MsalUser, profile: EmployeeProfile }

type Action =
  | { type: ActionType.GetUserProfile, msalUser: MsalUser }
  | { type: ActionType.UserProfileAcquired, profile: EmployeeProfile }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.GetUserProfile:
      return { step: Step.GettingUserProfile, msalUser: action.msalUser }

    case ActionType.UserProfileAcquired:
      if (state.step === Step.GettingUserProfile) {
        return { step: Step.UserDone, msalUser: state.msalUser, profile: action.profile }
      }
      break;
  }

  return state;
}

type Props = {
  config: MsalConfiguration;
  children: React.ReactNode;
}

export const KaEmployeeProvider: React.FC<Props> = ({ children, config }) => {
  const msalAccount = useMsalAuth(config);
  const [state, dispatch] = useReducer(reducer, { step: Step.NotAuthenticated });

  const fetchHeaders = async (user: MsalUser) => {
    if (user.expirationTime <= (Date.now() / 1000)) {
      user.signOut({ postLogoutUrl: window.location.pathname });
      return new Headers();
    }

    return new Headers({ 'Authorization': `Bearer ${(await user.getToken())}` });
  }

  const logout = (user: MsalUser) => {
    clearTracking();
    user.signOut({ postLogoutUrl: window.location.pathname });
  }

  if (state.step === Step.NotAuthenticated && msalAccount.user) {
    dispatch({ type: ActionType.GetUserProfile, msalUser: msalAccount.user });
  }

  useEffect(() => {
    if (state.step !== Step.GettingUserProfile) return;
    fetchHeaders(state.msalUser)
      .then(headers => fetch('/api/employee/users/me', { headers }))
      .then(response => response.json())
      .then((profile: EmployeeProfile) => {
        trackEmployeeSignIn(state.msalUser.id, state.msalUser.email);

        dispatch({
          type: ActionType.UserProfileAcquired,
          profile,
        });
      });
  }, [state]);

  function toUserStatus(): KaEmployeeStatus {
    if (msalAccount.busy) return { state: KaEmployeeState.Loading };

    switch (state.step) {
      case Step.UserDone:
        return {
          id: state.msalUser.id,
          email: state.msalUser.email,
          expirationTime: state.msalUser.expirationTime,
          canEditSites: state.profile.canEditSites,
          canViewOrders: state.profile.canViewOrders,
          state: KaEmployeeState.SignedIn,
          fetchHeaders: () => fetchHeaders(state.msalUser),
          signOut: () => logout(state.msalUser),
        };

      case Step.GettingUserProfile:
        return { state: KaEmployeeState.Loading };

      case Step.NotAuthenticated:
        return { state: KaEmployeeState.SignedOut, signIn: msalAccount.signIn };
    }
  }

  return (
    <KaEmployeeContext.Provider value={toUserStatus()}>
      {children}
    </KaEmployeeContext.Provider>
  );
};
